/**
 * Phase-specific educational content.
 *
 * Content lives in i18n under `phase_content.{phase}.*` and is resolved at
 * call time via `getPhaseContent(t)`. The English exports are kept as a
 * fallback so non-React contexts (transformers, scripts) can still read
 * canonical content without a translation function.
 */

import type { TreatmentPhaseName } from "../treatment-phases";
import type { TranslationFunction } from "@src/packages/i18n";

export interface PhaseContentData {
  title: string;
  description: string;
  keyFacts: string[];
  typicalDuration: string | null;
  whatHappens: string;
  whenToCall: string[];
}

export const PHASE_KEYS: TreatmentPhaseName[] = [
  "getting_started",
  "baseline",
  "stimulation",
  "trigger",
  "retrieval",
  "fertilization",
  "transfer",
  "waiting",
  "complete",
];

function tIndexedList(
  t: TranslationFunction,
  baseKey: string,
  maxItems = 12,
): string[] {
  const items: string[] = [];
  for (let i = 0; i < maxItems; i += 1) {
    const key = `${baseKey}.${i}`;
    const value = t(key);
    if (value === key) break;
    items.push(value);
  }
  return items;
}

export function getPhaseContent(
  phase: TreatmentPhaseName,
  t: TranslationFunction,
): PhaseContentData {
  const base = `phase_content.${phase}`;
  const typicalDuration = t(`${base}.typical_duration`);
  return {
    title: t(`${base}.title`),
    description: t(`${base}.description`),
    keyFacts: tIndexedList(t, `${base}.key_facts`),
    typicalDuration: typicalDuration ? typicalDuration : null,
    whatHappens: t(`${base}.what_happens`),
    whenToCall: tIndexedList(t, `${base}.when_to_call`),
  };
}

export function getAllPhaseContent(
  t: TranslationFunction,
): Record<TreatmentPhaseName, PhaseContentData> {
  return Object.fromEntries(
    PHASE_KEYS.map((phase) => [phase, getPhaseContent(phase, t)]),
  ) as Record<TreatmentPhaseName, PhaseContentData>;
}
